import React, { useState } from 'react';
import styled from 'styled-components';

const FormStyle = styled.form`
  .form-group {
    width: 100%;
    margin-bottom: 2rem;
  }
  label {
    font-size: 1.4rem;
    display: block;
    margin-bottom: 0.6rem;
    color: var(--gray-1);
    opacity: 0.75;
    text-transform: uppercase;
    letter-spacing: 0.08rem;
  }
  input,
  textarea,
  select {
    width: 100%;
    font-size: 1.6rem;
    padding: 1.2rem;
    color: var(--gray-1);
    background-color: var(--dark-bg);
    outline: none;
    border: 1px solid var(--gray-2);
    border-radius: 8px;
    transition: border-color 0.3s ease;
    font-family: 'RobotoMono Regular';
  }
  input:focus,
  textarea:focus,
  select:focus {
    border-color: var(--highlight-blue);
  }
  select {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='8' viewBox='0 0 14 8'%3E%3Cpath fill='none' stroke='%23BCB4B4' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1.4rem center;
    padding-right: 3.5rem;
    option {
      background-color: var(--dark-bg);
      color: var(--gray-1);
    }
  }
  textarea {
    min-height: 150px;
    resize: vertical;
  }
  .error {
    font-size: 1.3rem;
    color: #e63946;
    margin-top: 0.5rem;
  }
  button[type='submit'] {
    background-color: var(--highlight-blue);
    color: var(--dark-bg);
    font-size: 1.6rem;
    font-family: 'RobotoMono Regular';
    padding: 1.2rem;
    width: 100%;
    border-radius: 8px;
    cursor: pointer;
    border: none;
    transition: opacity 0.3s ease;
    letter-spacing: 0.1rem;
    text-transform: uppercase;
  }
  button[type='submit']:hover {
    opacity: 0.85;
  }
  button[type='submit']:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessStyles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  gap: 1.2rem;
  .success__icon {
    font-size: 4rem;
  }
  .success__heading {
    font-size: 2.4rem;
    font-family: 'Montserrat SemiBold';
    color: var(--highlight-blue);
  }
  .success__sub {
    font-size: 1.5rem;
    color: var(--gray-1);
    opacity: 0.7;
  }
  .success__reset {
    margin-top: 1.5rem;
    font-size: 1.4rem;
    color: var(--highlight-blue);
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    font-family: 'RobotoMono Regular';
    &:hover {
      opacity: 0.75;
    }
  }
`;

export default function ContactForm({ reason: selectedReason = '', onReasonChange }) {
  const [formData, setFormData] = useState({ name: '', email: '', reason: selectedReason, message: '' });

  // Keep form reason in sync when intent card changes
  React.useEffect(() => {
    setFormData((prev) => ({ ...prev, reason: selectedReason }));
  }, [selectedReason]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'reason' && onReasonChange) onReasonChange(value);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.reason) newErrors.reason = 'Please select a reason';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        access_key: 'ec8e5f72-daa8-44c2-ad9f-30aa95c3b3cd',
        subject: `Portfolio Contact: ${formData.reason}`,
        name: formData.name,
        email: formData.email,
        message: `Reason: ${formData.reason}\n\n${formData.message}`,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setFormData({ name: '', email: '', reason: '', message: '' });
          if (onReasonChange) onReasonChange('');
          setSubmitted(true);
        } else {
          setErrors({ submit: 'Something went wrong. Please try again.' });
        }
      })
      .catch(() => {
        setErrors({ submit: 'Network error. Please try again.' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (submitted) {
    return (
      <SuccessStyles>
        <span className="success__icon">✉️</span>
        <p className="success__heading">Message sent!</p>
        <p className="success__sub">Thanks for reaching out — I'll get back to you within 24 hours.</p>
        <button className="success__reset" onClick={() => setSubmitted(false)}>
          Send another message
        </button>
      </SuccessStyles>
    );
  }

  return (
    <FormStyle onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="name">Your Name</label>
        <input
          type="text"
          id="name"
          name="name"
          placeholder="Enter your name"
          value={formData.name}
          onChange={handleChange}
          aria-invalid={!!errors.name}
          aria-describedby="name-error"
        />
        {errors.name && <div id="name-error" className="error" role="alert">{errors.name}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="email">Your Email</label>
        <input
          type="email"
          id="email"
          name="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleChange}
          aria-invalid={!!errors.email}
          aria-describedby="email-error"
        />
        {errors.email && <div id="email-error" className="error" role="alert">{errors.email}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="reason">Reason for Contact</label>
        <select
          id="reason"
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          aria-invalid={!!errors.reason}
          aria-describedby="reason-error"
        >
          <option value="" disabled>Select a reason...</option>
          <option value="Freelance / Project Inquiry">Freelance / Project Inquiry</option>
          <option value="Job Opportunity">Job Opportunity</option>
          <option value="Open Source Collaboration">Open Source Collaboration</option>
          <option value="Technical Question">Technical Question</option>
          <option value="Just Saying Hi 👋">Just Saying Hi 👋</option>
          <option value="I Think I Broke Production 🔥">I Think I Broke Production 🔥</option>
        </select>
        {errors.reason && <div id="reason-error" className="error" role="alert">{errors.reason}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="message">Your Message</label>
        <textarea
          id="message"
          name="message"
          placeholder="Enter your message"
          value={formData.message}
          onChange={handleChange}
          aria-invalid={!!errors.message}
          aria-describedby="message-error"
        />
        {errors.message && <div id="message-error" className="error" role="alert">{errors.message}</div>}
      </div>

      {errors.submit && (
        <div className="error" role="alert" style={{ marginBottom: '1rem' }}>
          {errors.submit}
        </div>
      )}
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Sending...' : 'Send'}
      </button>
    </FormStyle>
  );
}
